import { Box, Button, ButtonText, Divider, HStack, Input, InputField, Text, VStack } from '@gluestack-ui/themed';
import { useNavigation } from '@react-navigation/native';
import { useFormik } from 'formik';
import { ArrowLeftIcon } from 'lucide-react-native';
import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Pressable, StyleSheet } from 'react-native';
import * as Yup from 'yup';

import { Image } from '@gluestack-ui/themed';
import Bioeureka from '../assets/bioeureka-black.svg';
import BackgroundSVG from '../assets/background.svg';
import { PrimaryButton } from '../components/buttons/PrimaryButton';
import { FormDatePicker } from '../components/form/FormDatePicker';
import { FormItemDropDown } from '../components/form/FormDropDown';
import { TranslationContext } from '../contexts/translations/Translation';
import { Routes } from '../routes/Routes';
import { COLORS } from '../theme/colors';
import { env } from '../utils/Env';

type Feedback = { type: 'success' | 'info' | 'error'; message?: string } | null;
type Institution = { name: string; domains: string[] };

export const RegisterPage: FC = () => {
  const { i18n, changeLanguage } = useContext(TranslationContext);
  const navigation = useNavigation<any>();
  const b = 'page.register';

  const isFr = (i18n?.locale ?? 'fr').startsWith('fr');
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [showNotListed, setShowNotListed] = useState(false);
  const [institution, setInstitution] = useState('');
  const [requestMessage, setRequestMessage] = useState('');
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [selectedInstitution, setSelectedInstitution] = useState('');
  const institutionRef = useRef('');

  // Load approved institutions for the dropdown.
  useEffect(() => {
    fetch(`${env.apiBaseUrl}/register/institutions`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data: Institution[]) => setInstitutions(Array.isArray(data) ? data : []))
      .catch(() => setInstitutions([]));
  }, []);

  const schema = Yup.object().shape({
    firstName: Yup.string().required(i18n?.t(`${b}.required`)),
    lastName: Yup.string().required(i18n?.t(`${b}.required`)),
    email: Yup.string().email(i18n?.t('page.login.invalid_email')).required(i18n?.t(`${b}.required`)),
    password: Yup.string()
      .min(8, i18n?.t(`${b}.password-too-short`))
      .matches(/(?=.*[A-Za-z])(?=.*\d)/, i18n?.t(`${b}.password-weak`))
      .required(i18n?.t(`${b}.required`)),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password')], i18n?.t(`${b}.password-mismatch`))
      .required(i18n?.t(`${b}.required`)),
  });

  const formik = useFormik({
    initialValues: { firstName: '', lastName: '', email: '', password: '', confirmPassword: '', studentUntil: undefined as string | undefined },
    validateOnChange: false,
    validationSchema: schema,
    onSubmit: async (values) => {
      setFeedback(null);
      setShowNotListed(false);
      try {
        const res = await fetch(`${env.apiBaseUrl}/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: values.email.trim().toLowerCase(),
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            password: values.password,
            studentUntil: values.studentUntil,
            language: (i18n?.locale ?? 'fr').startsWith('en') ? 'en' : 'fr',
            institution: institutionRef.current || undefined,
          }),
        });
        if (res.ok) {
          setFeedback({ type: 'success' });
          return;
        }
        if (res.status === 404) {
          setFeedback({ type: 'info', message: i18n?.t(`${b}.domain-not-approved`) });
          setShowNotListed(true);
          return;
        }
        if (res.status === 409) {
          setFeedback({ type: 'info', message: i18n?.t(`${b}.already-registered`) });
          return;
        }
        setFeedback({ type: 'error' });
      } catch (e) {
        setFeedback({ type: 'error' });
      }
    },
  });

  const emailDomain = formik.values.email.trim().toLowerCase().split('@')[1] ?? '';
  const detected = useMemo(
    () => institutions.find((i) => i.domains.includes(emailDomain)),
    [institutions, emailDomain],
  );
  const institutionOptions = institutions.map((i) => ({ label: i.name, value: i.name }));
  const mismatch = Boolean(selectedInstitution && detected && selectedInstitution !== detected.name);

  // Live password feedback (as the user types, independent of submit).
  const pw = formik.values.password;
  const cpw = formik.values.confirmPassword;
  const pwLenOk = pw.length >= 8;
  const pwAlnumOk = /[A-Za-z]/.test(pw) && /\d/.test(pw);
  const pwMatch = cpw.length > 0 && cpw === pw;
  const ruleColor = (started: boolean, ok: boolean) =>
    (!started ? COLORS.NEUTRAL[90] : ok ? COLORS.STATUS.SUCCESS : COLORS.STATUS.WARNING_2);

  // Auto-select the institution matched from the email domain.
  useEffect(() => {
    if (detected) setSelectedInstitution(detected.name);
  }, [detected?.name]);

  useEffect(() => {
    institutionRef.current = selectedInstitution;
  }, [selectedInstitution]);

  const submitInstitutionRequest = async () => {
    try {
      await fetch(`${env.apiBaseUrl}/register/request-institution`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formik.values.email.trim().toLowerCase(), institution, message: requestMessage }),
      });
      setShowNotListed(false);
      setFeedback({ type: 'success', message: i18n?.t(`${b}.request-sent`) });
    } catch (e) {
      setFeedback({ type: 'error' });
    }
  };

  const feedbackColor = feedback?.type === 'success' ? COLORS.STATUS.SUCCESS : feedback?.type === 'error' ? COLORS.STATUS.WARNING_2 : COLORS.TERCIARY[100];

  return (
    <Box style={styles.layout}>
      <Image alt="" style={styles.backgroundImage} source={{ uri: BackgroundSVG }} />

      <Pressable
        onPress={() => changeLanguage(isFr ? 'en' : 'fr')}
        style={{ position: 'absolute', top: 20, right: 24, borderWidth: 1, borderColor: COLORS.NEUTRAL[70], borderRadius: 8, paddingHorizontal: 12, paddingVertical: 6, backgroundColor: COLORS.NEUTRAL[0] }}
      >
        <Text fontSize={13} fontWeight="bold" color={COLORS.TERCIARY[100]}>{isFr ? 'EN' : 'FR'}</Text>
      </Pressable>

      <VStack style={{ width: '100%', maxWidth: 440 }}>
        <Pressable onPress={() => navigation.navigate(Routes.LOGIN)} style={{ marginBottom: 14 }}>
          <HStack gap={6} alignItems="center">
            <ArrowLeftIcon size={16} color={COLORS.ACCENT[100]} />
            <Text fontSize={13} color={COLORS.ACCENT[100]}>{i18n?.t(`${b}.back`)}</Text>
          </HStack>
        </Pressable>

        <Box backgroundColor={COLORS.NEUTRAL[0]} paddingVertical={30} paddingHorizontal={40} borderRadius={20}
          shadowColor="#171717" shadowOffset={{ width: -2, height: 4 }} shadowOpacity={0.2} shadowRadius={3}>
          <Image alt="Logo" source={{ uri: Bioeureka }} height={40} width={280} />
          <Text fontSize={18} fontWeight="bold" style={{ marginTop: 14 }}>{i18n?.t(`${b}.title`)}</Text>
          <Text fontSize={12} color={COLORS.NEUTRAL[90]} style={{ marginTop: 4 }}>{i18n?.t(`${b}.subtitle`)}</Text>
          <Divider marginVertical={18} />

          <HStack gap={12} flexWrap="wrap">
            <VStack style={{ flex: 1, minWidth: 150 }}>
              <Text fontSize={12} color={COLORS.NEUTRAL[90]} marginBottom={4}>{i18n?.t(`${b}.first-name`)}</Text>
              <Input><InputField value={formik.values.firstName} onChangeText={formik.handleChange('firstName')} autoCapitalize="words" /></Input>
              {formik.errors.firstName && <Text color={COLORS.STATUS.WARNING_2} fontSize={12}>{formik.errors.firstName}</Text>}
            </VStack>
            <VStack style={{ flex: 1, minWidth: 150 }}>
              <Text fontSize={12} color={COLORS.NEUTRAL[90]} marginBottom={4}>{i18n?.t(`${b}.last-name`)}</Text>
              <Input><InputField value={formik.values.lastName} onChangeText={formik.handleChange('lastName')} autoCapitalize="words" /></Input>
              {formik.errors.lastName && <Text color={COLORS.STATUS.WARNING_2} fontSize={12}>{formik.errors.lastName}</Text>}
            </VStack>
          </HStack>

          <Text fontSize={12} color={COLORS.NEUTRAL[90]} marginTop={14} marginBottom={4}>{i18n?.t(`${b}.email`)}</Text>
          <Input>
            <InputField value={formik.values.email} onChangeText={formik.handleChange('email')} keyboardType="email-address" autoCapitalize="none"
              onFocus={() => formik.setErrors({})} />
          </Input>
          {formik.errors.email && <Text color={COLORS.STATUS.WARNING_2} fontSize={12}>{formik.errors.email}</Text>}

          <VStack>
            <Text fontSize={12} color={COLORS.NEUTRAL[90]} marginTop={14} marginBottom={4}>{i18n?.t(`${b}.password`)}</Text>
            <Input><InputField secureTextEntry value={formik.values.password} onChangeText={formik.handleChange('password')} autoCapitalize="none" textContentType="newPassword" /></Input>
            <VStack style={{ marginTop: 4 }}>
              <Text fontSize={11} color={ruleColor(pw.length > 0, pwLenOk)}>
                {pwLenOk ? '✓' : '•'} {i18n?.t(`${b}.password-rule-length`)}
              </Text>
              <Text fontSize={11} color={ruleColor(pw.length > 0, pwAlnumOk)}>
                {pwAlnumOk ? '✓' : '•'} {i18n?.t(`${b}.password-rule-alnum`)}
              </Text>
            </VStack>
          </VStack>
          <VStack>
            <Text fontSize={12} color={COLORS.NEUTRAL[90]} marginTop={14} marginBottom={4}>{i18n?.t(`${b}.confirm-password`)}</Text>
            <Input><InputField secureTextEntry value={formik.values.confirmPassword} onChangeText={formik.handleChange('confirmPassword')} autoCapitalize="none" textContentType="newPassword" /></Input>
            {cpw.length > 0 && (
              <Text fontSize={12} color={pwMatch ? COLORS.STATUS.SUCCESS : COLORS.STATUS.WARNING_2}>
                {pwMatch ? `✓ ${i18n?.t(`${b}.password-match`)}` : i18n?.t(`${b}.password-mismatch`)}
              </Text>
            )}
          </VStack>

          {/* Institution: auto-matched from the email domain, clickable options only */}
          <Text fontSize={12} color={COLORS.NEUTRAL[90]} marginTop={14} marginBottom={4}>{i18n?.t(`${b}.institution-select-label`)}</Text>
          <FormItemDropDown
            value={selectedInstitution}
            onChange={setSelectedInstitution}
            options={institutionOptions}
            placeholder={i18n?.t(`${b}.institution-placeholder`)}
          />
          <Text fontSize={11} color={mismatch ? COLORS.STATUS.WARNING_2 : COLORS.NEUTRAL[90]} style={{ marginTop: 4 }}>
            {mismatch ? i18n?.t(`${b}.institution-mismatch`) : i18n?.t(`${b}.institution-hint`)}
          </Text>

          <Text fontSize={12} color={COLORS.NEUTRAL[90]} marginTop={14} marginBottom={4}>{i18n?.t(`${b}.language-label`)}</Text>
          <HStack gap={8}>
            {(['fr', 'en'] as const).map((lng) => {
              const active = isFr ? lng === 'fr' : lng === 'en';
              return (
                <Button
                  key={lng}
                  onPress={() => changeLanguage(lng)}
                  bg={active ? COLORS.ACCENT[100] : 'transparent'}
                  borderWidth={1}
                  borderColor={COLORS.ACCENT[100]}
                  rounded="$lg"
                  flex={1}
                  $hover-bg={active ? COLORS.ACCENT[110] : COLORS.NEUTRAL[60]}
                >
                  <ButtonText style={{ color: active ? COLORS.NEUTRAL[0] : COLORS.ACCENT[100] }}>
                    {lng === 'fr' ? 'Français' : 'English'}
                  </ButtonText>
                </Button>
              );
            })}
          </HStack>

          <Text fontSize={12} color={COLORS.NEUTRAL[90]} marginTop={14} marginBottom={4}>{i18n?.t(`${b}.student-until`)}</Text>
          <FormDatePicker
            value={formik.values.studentUntil ? new Date(formik.values.studentUntil) : undefined}
            onChange={(date?: Date) => formik.setFieldValue('studentUntil', date?.toISOString())}
          />

          <PrimaryButton style={{ marginTop: 26, alignSelf: 'center' }} isLoading={formik.isSubmitting}
            content={i18n?.t(`${b}.submit`)} onPress={async () => {
              if (mismatch) { setFeedback({ type: 'error', message: i18n?.t(`${b}.institution-mismatch`) }); return; }
              formik.handleSubmit();
            }} />

          {feedback && (
            <Text color={feedbackColor} fontSize={13} style={{ marginTop: 16, textAlign: 'center' }}>
              {feedback.message ?? i18n?.t(`${b}.${feedback.type}`)}
            </Text>
          )}

          {showNotListed && (
            <VStack style={{ marginTop: 18, borderTopWidth: 1, borderColor: COLORS.NEUTRAL[70], paddingTop: 16 }} gap={8}>
              <Text fontSize={13} fontWeight="bold">{i18n?.t(`${b}.not-listed-cta`)}</Text>
              <Input><InputField placeholder={i18n?.t(`${b}.institution`)} value={institution} onChangeText={setInstitution} /></Input>
              <Input><InputField placeholder={i18n?.t(`${b}.request-message`)} value={requestMessage} onChangeText={setRequestMessage} /></Input>
              <PrimaryButton style={{ marginTop: 6, alignSelf: 'center' }} content={i18n?.t(`${b}.request-submit`)}
                onPress={submitInstitutionRequest} />
            </VStack>
          )}
        </Box>
      </VStack>
    </Box>
  );
};

const styles = StyleSheet.create({
  layout: {
    backgroundColor: COLORS.FOUNDATION_PRIMARY,
    height: '100%',
    paddingHorizontal: '10%',
    paddingVertical: 48,
    flexDirection: 'column',
    display: 'flex',
    overflow: 'scroll',
    alignItems: 'center',
    // Anchor to the top (not centered) so a tall form never clips its own top —
    // otherwise you can't scroll up to the logo / "Back to login".
    justifyContent: 'flex-start',
  },
  backgroundImage: {
    position: 'absolute',
    width: 450,
    height: 450,
    top: 0,
    right: -60,
    zIndex: -1,
  },
});
